'use client';

import React, { useState } from 'react';
import { SportsbookLogo, SortIndicator, Tooltip, useBookCatalogContext } from '@sharpr/ui';
import type { SortField } from '@sharpr/types';
import type { SortDirection } from '@sharpr/ui';
import { cn } from '@sharpr/ui';
import { Search, X } from 'lucide-react';

interface TableHeaderProps {
  sportsbooks: string[];
  primaryColWidth: number;
  bestLineColWidth: number;
  sportsbookColWidth: number;
  totalWidth: number;
  sortField?: SortField | null;
  sortDirection?: SortDirection;
  onSort?: (field: SortField) => void;
  onReorderSportsbooks?: (newOrder: string[]) => void;
  isPanelCollapsed?: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
}

export const TableHeader = React.memo(function TableHeader({
  sportsbooks,
  primaryColWidth,
  bestLineColWidth,
  sportsbookColWidth,
  totalWidth,
  sortField,
  sortDirection,
  onSort,
  onReorderSportsbooks,
  isPanelCollapsed = false,
  searchQuery = '',
  onSearchChange
}: TableHeaderProps) {
  const catalog = useBookCatalogContext();
  // Drag-and-drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);

  // Drag event handlers
  const handleDragStart = (e: React.DragEvent, index: number, book: string) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', book);

    // Create ghost image (semi-transparent clone)
    if (e.currentTarget instanceof HTMLElement) {
      const ghost = e.currentTarget.cloneNode(true) as HTMLElement;
      ghost.style.opacity = '0.5';
      ghost.style.position = 'absolute';
      ghost.style.top = '-1000px';
      document.body.appendChild(ghost);
      e.dataTransfer.setDragImage(ghost, e.currentTarget.offsetWidth / 2, e.currentTarget.offsetHeight / 2);
      setTimeout(() => document.body.removeChild(ghost), 0);
    }
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && draggedIndex !== index) {
      setDropTargetIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDropTargetIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex || !onReorderSportsbooks) {
      setDraggedIndex(null);
      setDropTargetIndex(null);
      return;
    }

    // Reorder the sportsbooks array
    const newOrder = [...sportsbooks];
    const [movedBook] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(dropIndex, 0, movedBook);

    onReorderSportsbooks(newOrder);

    // Reset drag state
    setDraggedIndex(null);
    setDropTargetIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDropTargetIndex(null);
  };
  return (
    <div
      className="sticky top-0 bg-bg dark:bg-bg-primary z-40 border-b border-border dark:border-border-primary shadow-sm dark:shadow-sticky rounded-t-lg"
      style={{ width: totalWidth }}
    >
      <div className="flex h-14">
        {/* Primary info header - sticky left */}
        <div
          className="sticky left-0 bg-bg dark:bg-bg-primary z-50 border-r border-border dark:border-border-primary flex items-center px-4 rounded-tl-lg"
          style={{ width: primaryColWidth, minWidth: primaryColWidth }}
        >
          {isPanelCollapsed && onSearchChange && (
            /* Expanded search bar when panel collapsed */
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-text-tertiary" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search players or teams"
                className={cn(
                  "w-full pl-9 h-7 sm:h-auto sm:py-1.5 text-sm leading-none sm:leading-normal",
                  searchQuery ? "pr-9" : "pr-3",
                  "bg-white dark:bg-bg-tertiary text-gray-900 dark:text-text-primary",
                  "border border-gray-200 dark:border-border-primary rounded-lg",
                  "focus:outline-none focus:border-gray-300 dark:focus:border-border-interactive",
                  "placeholder:text-gray-400 dark:placeholder:text-text-tertiary"
                )}
              />
              {searchQuery && (
                <button
                  onClick={() => onSearchChange('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-text-tertiary hover:text-gray-600 dark:hover:text-text-secondary transition-colors"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Best line header - sticky left */}
        <div
          className="sticky bg-bg dark:bg-bg-primary z-40 border-r border-border dark:border-border-primary flex items-center"
          style={{ left: primaryColWidth, width: bestLineColWidth, minWidth: bestLineColWidth }}
        >
          {onSort ? (
            <button
              onClick={() => onSort('best_ev')}
              className="w-full h-full flex items-center justify-center gap-1 px-4 cursor-pointer hover:bg-muted/50 active:bg-muted/70 transition-colors duration-200"
              aria-label={`Best Line${sortField === 'best_ev' ? `, currently sorted ${sortDirection === 'desc' ? 'descending' : 'ascending'}. Click to change sort order.` : ', not sorted. Click to sort.'}`}
              aria-sort={sortField === 'best_ev' ? (sortDirection === 'desc' ? 'descending' : 'ascending') : 'none'}
            >
              <span className="font-medium text-muted-foreground dark:text-text-secondary">
                Best Line
              </span>
              {/* Indicator beside text - only show when active */}
              {sortField === 'best_ev' && sortDirection && (
                <SortIndicator
                  direction={sortDirection}
                  isActive={true}
                  size="sm"
                />
              )}
            </button>
          ) : (
            <div className="w-full px-4 py-3 text-center font-medium text-muted-foreground dark:text-text-secondary">
              Best Line
            </div>
          )}
        </div>

        {/* Sportsbook headers */}
        {sportsbooks.map((book, index) => {
          const dfs = catalog.get(book)?.dfs;
          const tooltip = !dfs ? undefined : dfs.impliedOdds !== undefined ? `Odds shown as ${dfs.impliedOdds} (${dfs.entryLabel})` : `Odds shown as ${dfs.entryLabel}`;
          return (
          <div
            key={book}
            draggable={onReorderSportsbooks !== undefined}
            onDragStart={(e) => onReorderSportsbooks && handleDragStart(e, index, book)}
            onDragOver={(e) => onReorderSportsbooks && handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => onReorderSportsbooks && handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            data-tour={index === 0 ? 'tour-demo-sportsbook-col' : undefined}
            className={cn(
              "border-r border-border dark:border-border-primary relative transition-all duration-200",
              draggedIndex === index && "opacity-50",
              dropTargetIndex === index && "bg-blue-500/10 border-l-2 border-l-blue-600"
            )}
            style={{
              width: sportsbookColWidth,
              minWidth: sportsbookColWidth,
              cursor: onReorderSportsbooks ? 'grab' : 'default'
            }}
          >
            {onSort ? (
              <button
                onClick={() => onSort(book)}
                className="w-full h-full flex items-center justify-center gap-1 px-1 cursor-pointer hover:bg-muted/50 active:bg-muted/70 transition-colors duration-200"
                aria-label={`Sort by ${catalog.get(book)?.name ?? book}${sortField === book ? ', currently sorted descending. Click to return to Best EV sort.' : ', not sorted. Click to sort.'}`}
                aria-sort={sortField === book ? 'descending' : 'none'}
                style={{ cursor: onReorderSportsbooks ? 'grab' : 'pointer' }}
              >
                {tooltip ? (
                  <Tooltip content={tooltip} side="bottom">
                    <SportsbookLogo book={book} size="sm" showText={false} />
                  </Tooltip>
                ) : (
                  <SportsbookLogo book={book} size="sm" showText={false} />
                )}
                {sortField === book && (
                  <SortIndicator direction="desc" isActive={true} size="sm" />
                )}
              </button>
            ) : (
              <div className="w-full h-full flex items-center justify-center px-1">
                {tooltip ? (
                  <Tooltip content={tooltip} side="bottom">
                    <SportsbookLogo book={book} size="sm" showText={false} />
                  </Tooltip>
                ) : (
                  <SportsbookLogo book={book} size="sm" showText={false} />
                )}
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
});